import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { CheckOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Button, Badge, Spin } from 'antd';
import axios from 'axios';
import { scoutChatCenterServerUrl } from '../../../../autoConfig';

const ChatList = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [chatList, setChatList] = useState([]);
  const { messages } = useSelector((state) => state.messageState);
  const [permission, setPermission] = useState(Notification.permission);
  const { id } = useParams();
  const [newMessageList, setNewMessageList] = useState(['']);
  const [loading, setLoading] = useState(true);
  const lastNotificationTimeRef = useRef(0);

  // Request permission for notifications
  const requestNotificationPermission = () => {
    if (
      Notification.permission === 'default' ||
      Notification.permission === 'denied'
    ) {
      Notification.requestPermission().then((permission) => {
        setPermission(permission);
      });
    }
  };

  const sentTeamsNotification = async () => {
    if (JSON.parse(localStorage.getItem('notificationsEnabled'))) {
      try {
        const response = await axios.post(
          'https://scout-middleware-global.azurewebsites.net/sendNotification',
          {
            notificationType: 'textmessage',
            message:
              'A new chat is awaiting your attention! 🔔 **Please review it in the Scout Chat Center.**',
            sendto: [sessionStorage.getItem('teamsID')]
          }
        );
      } catch (error) {
        console.error('Error sending notification:', error);
      }
    }
  };

  // Display the notification with throttling
  const showNotification = () => {
    const now = Date.now();
    if (
      permission === 'granted' &&
      now - lastNotificationTimeRef.current > 40000
    ) {
      let notification = new Notification(
        'New chat waiting in the Scout Chat Center'
      );
      notification.onclick = function (event) {
        window.focus();
      };
      sentTeamsNotification();
      lastNotificationTimeRef.current = now;
    } else {
      console.warn('Permission not granted or notification throttled.');
    }
  };

  let ChatSession;
  let checkChatStatusInterval;

  function startSSE() {
    if (ChatSession) {
      ChatSession.close();
    }
    setLoading(true);
    ChatSession = new EventSource(
      `${scoutChatCenterServerUrl}/chatSession/setupSSE?consultantEmail=${name}`
    );

    ChatSession.addEventListener('message', (event) => {
      if (Array.isArray(JSON.parse(event.data))) {
        const curList = JSON.parse(sessionStorage.getItem('chatList'))
          ? JSON.parse(sessionStorage.getItem('chatList')).map(
              (item) => item.chatId
            )
          : [];
        const comingList = JSON.parse(event.data);
        if (
          comingList.some(
            (item) =>
              item.status !== 'processing' && !curList.includes(item.chatId)
          )
        ) {
          showNotification();
        }
        setChatList(JSON.parse(event.data));
        sessionStorage.setItem('chatList', event.data);
        setLoading(false);
      }
    });

    ChatSession.onerror = (event) => {
      console.error('SSE error:', event);
      if (ChatSession.readyState === EventSource.CLOSED) {
        setTimeout(startSSE, 10000);
      }
    };

    if (checkChatStatusInterval) {
      clearInterval(checkChatStatusInterval);
    }

    // Set up a new interval to check chat statuses every 3 minutes
    checkChatStatusInterval = setInterval(() => {
      const chatList = JSON.parse(sessionStorage.getItem('chatList'));
      if (chatList) {
        chatList.forEach((chat) => {
          if (chat.status !== 'processing') {
            showNotification();
          }
        });
      }
    }, 50000);
  }

  useEffect(() => {
    requestNotificationPermission();
    localStorage.getItem('username') &&
      setName(JSON.parse(localStorage.getItem('username'))[0].value);
    if (name !== '') {
      startSSE();
    }
  }, [name]);

  useEffect(() => {
    if (messages.slice(-1)[0] && messages.slice(-1)[0]?.chatId !== id) {
      setNewMessageList((prevList) => [
        ...prevList,
        messages.slice(-1)[0].chatId
      ]);
    }
  }, [messages]);

  useEffect(() => {
    setNewMessageList((prevList) => prevList.filter((chatId) => chatId !== id));
  }, [id]);

  return (
    <div
      style={{
        position: 'relative',
        height: '80vh',
        overflow: 'auto'
      }}
    >
      <Spin spinning={loading} style={{ top: '48px' }} size="large">
        <div style={{ visibility: loading ? 'hidden' : 'visible' }}>
          {chatList.map((item, idx) => {
            return (
              <div
                key={idx}
                className={`w-full flex justify-between p-4 ${
                  item.consultantEmails[item.consultantEmails.length - 1] ===
                  name
                    ? `cursor-pointer`
                    : `cursor-default`
                } hover:bg-slate-200 ${
                  item.chatId === id ? `bg-slate-100` : ``
                }`}
                onClick={() => {
                  item.consultantEmails[item.consultantEmails.length - 1] ===
                    name && navigate('/chat/' + item.chatId);
                }}
              >
                <div>
                  <Badge
                    key={idx}
                    className="w-full"
                    count={
                      newMessageList.includes(item.chatId) ? (
                        <InfoCircleFilled
                          style={{
                            color: '#f5222d',
                            fontSize: '20px'
                          }}
                        />
                      ) : null
                    }
                    showZero
                  ></Badge>
                  {item.status === 'processing' ? (
                    <Badge
                      status="processing"
                      text={`Processed by ${
                        item.consultantEmails[item.consultantEmails.length - 1]
                      }`}
                    />
                  ) : (
                    <Badge status="error" text="Waiting" />
                  )}

                  <div className="mt-1">
                    {dayjs(item.createdTime).format('ddd, MMM D, h:mm A')}
                  </div>
                  <div className="flex mt-1">
                    <div className="mr-2">Client Name:</div>
                    <div className="font-semibold">{item.clientName}</div>
                  </div>
                  <div className="flex mt-1">
                    <div className="mr-2">Client Email:</div>
                    <div className="font-semibold">{item.clientEmail}</div>
                  </div>
                  <div className="flex mt-1">
                    <div className="mr-2">Client DebtorId:</div>
                    <div className="font-semibold">{item.clientDebtorId}</div>
                  </div>
                  <div className="flex mt-1">
                    <div className="mr-2">Client Company:</div>
                    <div className="font-semibold">{item.debtorName}</div>
                  </div>
                  <div className="flex mt-1">
                    <div className="mr-2">Team:</div>
                    <div className="font-semibold">
                      {item.teamNames.toString()}
                    </div>
                  </div>
                  <div className="flex mt-1">
                    <div className="mr-2">Reason:</div>
                    <div className="font-semibold">{item.reason}</div>
                  </div>
                  {item.status === 'released' ? (
                    <div className="flex mt-1">
                      <div className="mr-2 text-red-500">Released By:</div>
                      <div className="font-semibold">
                        {
                          item.consultantEmails[
                            item.consultantEmails.length - 1
                          ]
                        }
                      </div>
                    </div>
                  ) : null}
                  {item.status === 'released' ? (
                    <div className="flex mt-1">
                      <div className="mr-2 text-red-500">Release Reason:</div>
                      <div className="font-semibold">{item.releaseReason}</div>
                    </div>
                  ) : null}
                </div>

                {item.status !== 'processing' ? (
                  <Button
                    shape="circle"
                    onClick={() => {
                      navigate('/chat/' + item.chatId);
                    }}
                  >
                    <CheckOutlined />
                  </Button>
                ) : (
                  <Button
                    shape="circle"
                    disabled
                    onClick={async () => {
                      navigate('/chat/' + item.chatId);
                    }}
                  >
                    <CheckOutlined />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Spin>
    </div>
  );
};

export default ChatList;
